//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { ProductDetailsModalContent } from '@campaigns/components/shared/product-details-modal/modal/ProductDetailsModalContent';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  ProductModalMode,
  useProductModal,
} from '@campaigns/contexts/ProductModalContext';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import {
  isMissingDetails,
  prepareProductToSubmit,
} from '@campaigns/utils/sampling/SamplingProductUtils';
import { Modal } from '@components/molecules/modal/Modal';

export const ProductDetailsModal = () => {
  const {
    addProductToCampaign,
    closeProductDetailsModal,
    isProductDetailsModalOpen,
    productModalState,
    uploadImageFilesFromProduct,
  } = useProductModal();

  const { action, mode, onProductSave, product } = productModalState;
  const isViewMode = mode === ProductModalMode.View;
  const isEditMode = mode === ProductModalMode.Edit;
  const hasRequiredDetails = !isMissingDetails(product);

  const methods = useForm<EditableBVSamplingProduct>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      ...productModalState.product,
      // Only set newProduct to true if in create mode or if explicitly set to true on the product
      newProduct:
        mode === ProductModalMode.Create ||
        productModalState.product?.newProduct,
      variations: productModalState.product?.variations ?? [],
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasInitialValidationTriggered, setHasInitialValidationTriggered] =
    useState(false);

  const handleProductUpdate = useCallback(
    (productToSubmit: BVSamplingProduct) => {
      const isProductMissingDetails = isMissingDetails(productToSubmit);

      if (isProductMissingDetails) {
        return;
      }

      if (action === ProductModalMode.Edit && onProductSave) {
        if (!onProductSave(productToSubmit)) {
          return;
        }
      } else if (addProductToCampaign) {
        addProductToCampaign(
          productToSubmit,
          action === ProductModalMode.Edit,
          productModalState.originalProductId,
        );
      }

      closeProductDetailsModal();
    },
    [
      closeProductDetailsModal,
      addProductToCampaign,
      action,
      onProductSave,
      productModalState,
    ],
  );

  const onSubmit = useCallback(
    async (product: EditableBVSamplingProduct) => {
      setIsSubmitting(true);
      try {
        // Upload images and update product
        // Note: Error handling is done in useUploadProductImagesMutation
        // which shows a toast notification if the upload fails
        const dataWithUploadedImages =
          await uploadImageFilesFromProduct(product);
        const preparedProduct = prepareProductToSubmit(dataWithUploadedImages);
        handleProductUpdate(preparedProduct);
      } finally {
        setIsSubmitting(false);
      }
    },
    [handleProductUpdate, uploadImageFilesFromProduct],
  );

  useEffect(() => {
    const shouldTriggerValidation =
      isEditMode && !hasRequiredDetails && !hasInitialValidationTriggered;

    if (shouldTriggerValidation) {
      setHasInitialValidationTriggered(true);
      void methods.handleSubmit(onSubmit)();
    }
  }, [
    isEditMode,
    hasRequiredDetails,
    hasInitialValidationTriggered,
    methods,
    onSubmit,
  ]);

  if (!isProductDetailsModalOpen) {
    return null;
  }

  const modalSharedProps = {
    closeModal: closeProductDetailsModal,
    isModalOpen: isProductDetailsModalOpen,
    width: 'extra-wide' as const satisfies React.ComponentPropsWithoutRef<
      typeof Modal
    >['width'],
  };

  const renderViewModeModal = () => (
    <StyledModal
      {...modalSharedProps}
      contents={<ProductDetailsModalContent isViewOnly={true} />}
      primaryButtonInfo={{
        buttonContent: 'Close',
        onClick: closeProductDetailsModal,
        trackingId: SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_CANCEL_BUTTON,
      }}
      shouldIncludeSecondaryButton={false}
      shouldIncludeTertiaryButton={false}
      title='View Product'
    />
  );

  const renderEditCreateModal = () => (
    <StyledModal
      {...modalSharedProps}
      contents={<ProductDetailsModalContent isViewOnly={false} />}
      primaryButtonInfo={{
        buttonContent:
          mode === ProductModalMode.Create ? 'Create Product' : 'Save Product',
        trackingId: SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_SUBMIT_BUTTON,
        onClick: methods.handleSubmit(onSubmit),
        isDisabled: isSubmitting,
      }}
      tertiaryButtonInfo={{
        buttonContent: 'Cancel',
        onClick: closeProductDetailsModal,
        trackingId: SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_CANCEL_BUTTON,
        isDisabled: isSubmitting,
      }}
      title={
        mode === ProductModalMode.Create ? 'Create New Product' : 'Edit Product'
      }
    />
  );

  return (
    <FormProvider {...methods}>
      {isViewMode ? renderViewModeModal() : renderEditCreateModal()}
    </FormProvider>
  );
};

const StyledModal = styled(Modal)`
  max-height: ${({ theme }) => theme.extraWideMeasure};
`;
